import { Avatar } from "../../../../core/Avatar.js";
import { Button } from "../../../../core/Button.js";
import { cardStyles } from "../../../../core/Card.styles.js";
import { Text } from "../../../../core/Text.js";
import { Amount } from "../../../../domain/Amount.js";
import { cn } from "../../../../foundations/cn.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import type { AccountHeaderContent } from "../../accountContent.js";

/**
 * Who the shopper is and what they can spend, above every tab of their account: the avatar,
 * name and public profile on the left, the balance and Deposit on the right (beneath, on phones).
 */
export function AccountHeader({
  name,
  named,
  avatarName,
  detail,
  profile,
  balance,
  deposit,
}: AccountHeaderContent) {
  return (
    <div
      className={cardStyles({
        radius: "2xl",
        className: "flex flex-col gap-6 p-5 md:flex-row md:items-center md:justify-between md:p-8",
      })}
    >
      <div className="flex min-w-0 items-center gap-4 md:gap-6">
        <Avatar name={avatarName} size="lg" decorative className="md:size-24" />
        <div className="flex min-w-0 flex-col items-start gap-2">
          <Text
            as="h1"
            variant="display-xl3"
            tone={named ? "primary" : "tertiary"}
            className="max-w-full truncate"
          >
            {name}
          </Text>
          {detail && (
            <Text variant="body-b2" tone="secondary">
              {detail}
            </Text>
          )}
          {profile && (
            <Button
              variant="secondary"
              size="sm"
              disabled={profile.disabled}
              onClick={profile.onClick}
            >
              {profile.label}
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 md:items-end">
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <Text variant="caption-eyebrow" tone="tertiary" className="uppercase md:sr-only">
              {balance.label}
            </Text>
            <span
              className={cn(
                "text-text-primary md:rounded-pill md:bg-bg-inset md:px-4 md:py-2",
                balance.cents === null && "text-text-tertiary",
              )}
            >
              {balance.cents === null ? (
                <span className="font-mono text-mono-price-l">—</span>
              ) : (
                <Amount cents={balance.cents} size="lg" />
              )}
            </span>
          </div>
          {deposit && <ActionButton action={deposit} variant="primary" />}
        </div>
        {balance.held && (
          <Text variant="body-b3" tone="secondary" className="max-w-xs md:text-end">
            {balance.held}
          </Text>
        )}
      </div>
    </div>
  );
}
